import React from 'react';
import { useTranslation } from 'react-i18next';
import { fmt } from '../data/metrics';
import { OPENTABLE_WINDOW_LABEL } from '../data/openTableBenchmark';
import { formatNorthStarValue } from '../lib/northStarFormat';
import type {
  NorthStarPayload,
  NorthStarSpec,
  OpsCommandPerformancePayload,
  Outlet,
  PerformanceMetric,
  ScopeGroupDataSource,
} from '../types';
import type { ComparisonItem } from './Topbar';
import { Delta } from './shared/Delta';
import { Icon } from './shared/Icon';
import { PerformanceLoader } from './PerformanceLoader';

type ComparisonPerfItem = ComparisonItem & {
  perf: OpsCommandPerformancePayload | null;
  northStar?: NorthStarPayload | null;
  dataSource?: ScopeGroupDataSource;
};

function isOpenTableItem(item: ComparisonPerfItem): boolean {
  return item.dataSource === 'opentable';
}

// Same chapters as the original Performance scorecard; guests joins Demand
// because comparison mode has no guests banner.
const SCORE_GROUPS: { id: string; keys: string[] }[] = [
  {
    id: 'demand',
    keys: ['covers', 'reservations', 'noshow', 'cancel'],
  },
  {
    id: 'efficiency',
    keys: ['occupancy', 'brainAutoPct', 'turns', 'seatTime'],
  },
  {
    id: 'money',
    keys: ['sellitems', 'revenue', 'avgCheck', 'revpash'],
  },
];

function relDelta(actual: number, ref: number): number | null {
  if (!ref) return null;
  return +(((actual / ref) - 1) * 100).toFixed(1);
}

function fmtMetric(m: PerformanceMetric): string {
  const v = m.value;
  if (m.unit === '%') return `${v}%`;
  if (m.unit === '×') return `${v}×`;
  if (m.unit === 'min') return `${v}m`;
  if (m.unit === '€') return fmt.money(v);
  return fmt.num(v);
}

type ScopeSummary = {
  item: ComparisonPerfItem;
  outletCount: number;
  seats: number;
  covers: number;
  reservations: number;
  coversPerOutlet: number;
  reservationsPerOutlet: number;
  coversPerSeat: number;
  noShow: number;
  cancel: number;
  latestCovers: number;
  previousCovers: number;
  latestCoversPerOutlet: number;
  momentum: number | null;
  share: number;
};

function safeDiv(n: number, d: number): number {
  return d ? n / d : 0;
}

function activeOutlets(outlets: Outlet[]): Outlet[] {
  const open = outlets.filter(o => !o.closed);
  return open.length ? open : outlets;
}

function metricFor(item: ComparisonPerfItem, key: string): PerformanceMetric | undefined {
  return item.perf?.metrics?.find(m => m.key === key);
}

function metricValue(item: ComparisonPerfItem, key: string): number {
  const m = metricFor(item, key);
  return m && !m.locked ? m.value : 0;
}

function metricLatest(item: ComparisonPerfItem, key: string): number {
  const m = metricFor(item, key);
  const series = m?.series ?? [];
  if (!m || m.locked) return 0;
  return series.length ? series[series.length - 1] : m.value;
}

function metricPrevious(item: ComparisonPerfItem, key: string): number {
  const m = metricFor(item, key);
  const series = m?.series ?? [];
  if (!m || m.locked) return 0;
  return series.length > 1 ? series[series.length - 2] : m.prior;
}

function buildScopeSummaries(items: ComparisonPerfItem[]): ScopeSummary[] {
  const base = items.map(item => {
    const outlets = activeOutlets(item.perf?.outlets ?? []);
    const outletCount = outlets.length || 1;
    const seats = outlets.reduce((a, o) => a + (o.seats || 0), 0) || 1;
    const covers = metricValue(item, 'covers');
    const reservations = metricValue(item, 'reservations');
    const latestCovers = metricLatest(item, 'covers');
    const previousCovers = metricPrevious(item, 'covers');
    return {
      item,
      outletCount,
      seats,
      covers,
      reservations,
      coversPerOutlet: safeDiv(covers, outletCount),
      reservationsPerOutlet: safeDiv(reservations, outletCount),
      coversPerSeat: safeDiv(covers, seats),
      noShow: metricValue(item, 'noshow'),
      cancel: metricValue(item, 'cancel'),
      latestCovers,
      previousCovers,
      latestCoversPerOutlet: safeDiv(latestCovers, outletCount),
      momentum: relDelta(latestCovers, previousCovers),
      share: 0,
    };
  });
  const totalCovers = base.reduce((a, s) => a + s.covers, 0);
  return base.map(s => ({
    ...s,
    share: totalCovers ? +((s.covers / totalCovers) * 100).toFixed(1) : 0,
  }));
}

function benchmarkSummary(summaries: ScopeSummary[]): ScopeSummary | undefined {
  const candidates = summaries.filter(s => !isOpenTableItem(s.item));
  const pool = candidates.length ? candidates : summaries;
  return (
    pool.find(s => s.item.filter === 'all') ??
    pool.slice().sort((a, b) => b.outletCount - a.outletCount)[0]
  );
}

function signedPct(actual: number, reference: number): number | null {
  if (!reference) return null;
  return +(((actual / reference) - 1) * 100).toFixed(1);
}

function signedPp(actual: number, reference: number): number {
  return +(actual - reference).toFixed(1);
}

function fmtOne(v: number): string {
  return v.toLocaleString('de-CH', { maximumFractionDigits: 1 });
}

function fmtTwo(v: number): string {
  return v.toLocaleString('de-CH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function northStarDeltaInvert(spec: NorthStarSpec): boolean {
  if (spec.composition === 'single') {
    return spec.components[0]?.direction === 'down';
  }
  return false;
}

function fmtNorthStar(payload: NorthStarPayload): string {
  const { value, suffix } = formatNorthStarValue(
    payload.currentValue,
    payload.spec.unit,
  );
  return suffix === '%' || suffix === '×' ? `${value}${suffix}` : `${value}${suffix ? ` ${suffix}` : ''}`;
}

// Comparison rendering reuses the original Performance-tab building blocks
// (perf-bar, scard tiles, ol-card, histo) with one compact row per compared
// scope inside each tile. Scope color appears only as a small dot.
export function ComparisonPerfView({
  items,
  loading,
}: {
  items: ComparisonPerfItem[];
  loading: boolean;
}) {
  const { t } = useTranslation('performance');
  const ready = items.filter(i => i.perf != null);
  // Wait for every compared scope: shares and the benchmark are read across
  // all of them, and scopes arriving one by one look like a finished screen
  // that then grows.
  if (loading) {
    return <PerformanceLoader label={t('compare.loading')} />;
  }

  const first = ready[0]?.perf;
  const summaries = buildScopeSummaries(ready);
  const benchmark = benchmarkSummary(summaries);

  return (
    <div className="perf" data-ftue="comparison-view">
      <CompareNorthStar items={ready} />
      <ComparisonLens summaries={summaries} benchmark={benchmark} />

      {/* ---------- scorecard: original tiles, rows per scope ---------- */}
      <div className="sec-head">
        <h2>{t('scorecardTitle')}</h2>
        <span className="sh-meta">
          {t('compare.vsPeriod', {
            compare: first?.comparePeriod?.label ?? t('compare.priorPeriod'),
            period: first?.periodLabel,
          })}
        </span>
        <span
          className="prov-note"
          title={t('compare.fromBookingsTip')}
        >
          {t('compare.fromBookings')}
        </span>
      </div>
      {SCORE_GROUPS.map(group => {
        const keys = group.keys.filter(k =>
          ready.some(i => {
            const m = metricFor(i, k);
            return m && !m.locked;
          }),
        );
        if (!keys.length) return null;
        return (
          <div className="scard-group" key={group.id}>
            <div className="scard-group-lab">{t(`groups.${group.id}`)}</div>
            <div className="scards">
              {keys.map(key => {
                const label =
                  ready
                    .map(i => metricFor(i, key)?.label)
                    .find(l => l != null) ?? key;
                const invert =
                  key === 'noshow' || key === 'cancel' || key === 'rejected';
                return (
                  <div key={key} className="scard">
                    <div className="sc-lab">
                      <Icon name="table" size={14} className="ic" />
                      {label}
                    </div>
                    <div className="cmpx-rows">
                      {ready.map(item => {
                        const m = metricFor(item, key);
                        const summary = summaries.find(
                          s => s.item.filter === item.filter,
                        );
                        return (
                          <div key={item.filter} className="cmpx-row">
                            <span
                              className="cmp-chip-dot"
                              style={{ background: item.color }}
                            />
                            <span className="cmpx-name">{item.label}</span>
                            <span className="cmpx-vals">
                              {m && !m.locked ? (
                                <>
                                  <span>{fmtMetric(m)}</span>
                                  <Delta
                                    value={relDelta(m.value, m.prior)}
                                    invert={invert}
                                    size={11}
                                  />
                                </>
                              ) : isOpenTableItem(item) ? (
                                <span className="cmpx-sub">{t('compare.na')}</span>
                              ) : (
                                <span className="cmpx-sub">-</span>
                              )}
                            </span>
                            {m && !m.locked && summary && (
                              <MetricContext
                                metric={m}
                                summary={summary}
                                benchmark={benchmark}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* ---------- outlets: one combined table, scope as a dot ---------- */}
      <div className="sec-head" style={{ marginTop: 22 }}>
        <h2>{t('outletPerf.title')}</h2>
        <span className="sh-meta">
          {t('compare.topOutlets', { period: first?.periodLabel })}
        </span>
        <span
          className="prov-note"
          title={t('compare.fromBookingsPeriodTip')}
        >
          {t('compare.fromBookings')}
        </span>
      </div>
      <CompareOutletTable items={ready} />

      {/* ---------- over time: per-bucket histogram, grouped per scope ---------- */}
      <div className="act-head">{t('compare.overTime')}</div>
      <CompareSeries items={ready} />
    </div>
  );
}

function CompareNorthStar({ items }: { items: ComparisonPerfItem[] }) {
  const { t } = useTranslation('performance');
  const anchor = items.find(i => i.northStar)?.northStar;
  if (!anchor) return null;

  const invert = northStarDeltaInvert(anchor.spec);
  const first = items.find(i => i.perf)?.perf;

  return (
    <>
      <div className="sec-head">
        <h2>{t('northStar.kicker')}</h2>
        <span className="sh-meta">
          {t('compare.vsPeriod', {
            compare: first?.comparePeriod?.label ?? t('compare.priorPeriod'),
            period: first?.periodLabel,
          })}
        </span>
      </div>
      <div className="scard cmpx-ns-card">
        <div className="sc-lab">
          <Icon name="star" size={14} className="ic" />
          {anchor.label}
        </div>
        {anchor.description && (
          <p className="cmpx-ns-desc">{anchor.description}</p>
        )}
        <div className="cmpx-rows">
          {items.map(item => {
            const ns = item.northStar;
            return (
              <div key={item.filter} className="cmpx-row">
                <span
                  className="cmp-chip-dot"
                  style={{ background: item.color }}
                />
                <span className="cmpx-name">{item.label}</span>
                <span className="cmpx-vals">
                  {ns ? (
                    <>
                      <span>{fmtNorthStar(ns)}</span>
                      <Delta
                        value={ns.trendPct}
                        invert={invert}
                        size={11}
                      />
                    </>
                  ) : isOpenTableItem(item) ? (
                    <span className="cmpx-sub">{t('compare.na')}</span>
                  ) : (
                    <span className="cmpx-sub">-</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
        {anchor.componentBreakdown.length > 1 && (
          <div className="cmpx-ns-components">
            {anchor.componentBreakdown.map(c => (
              <span key={c.metricKey} className="ns-comp-chip">
                <span className="ns-comp-label">{c.label}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function ComparisonLens({
  summaries,
  benchmark,
}: {
  summaries: ScopeSummary[];
  benchmark?: ScopeSummary;
}) {
  const { t } = useTranslation('performance');
  if (!summaries.length) return null;
  const benchLabel = benchmark?.item.label ?? t('compare.largestScope');
  return (
    <>
      <div className="sec-head">
        <h2>{t('compare.fairLens')}</h2>
        <span className="sh-meta">
          {t('compare.fairMeta', { bench: benchLabel })}
        </span>
      </div>
      <div className="ol-card cmpx-lens-card">
        <div className="cmpx-lens-note">
          {t('compare.lensNote')}
        </div>
        <div className="cmpx-lens-scroll">
          <div className="cmpx-lens-head">
            <span>{t('compare.scope')}</span>
            <span>{t('compare.scale')}</span>
            <span>{t('compare.totalGuests')}</span>
            <span>{t('compare.guestsOutlet')}</span>
            <span>{t('compare.guestsSeat')}</span>
            <span>{t('compare.quality')}</span>
            <span>{t('compare.latestPeriod', { unit: summaries[0]?.item.perf?.aggregationLabel ?? t('compare.period') })}</span>
          </div>
          {summaries.map(s => (
            <div className="cmpx-lens-row" key={s.item.filter}>
              <div className="cmpx-lens-scope">
                <span
                  className="cmp-chip-dot"
                  style={{ background: s.item.color }}
                />
                <div>
                  <b>
                    {s.item.label}
                    {isOpenTableItem(s.item) && (
                      <span className="scope-source-tag" title={t('compare.opentableTip')}>
                        OpenTable · {OPENTABLE_WINDOW_LABEL}
                      </span>
                    )}
                  </b>
                  <span>
                    {isOpenTableItem(s.item)
                      ? t('compare.aggregateOnly', { count: s.outletCount })
                      : t('compare.shareCompared', { share: s.share })}
                  </span>
                </div>
              </div>
              <div className="cmpx-lens-cell">
                <b>{s.outletCount}</b>
                <span>{t('compare.seats', { count: fmt.num(s.seats) })}</span>
              </div>
              <div className="cmpx-lens-cell">
                <b>{fmt.num(s.covers)}</b>
                <span>{t('compare.reservations', { count: fmt.num(s.reservations) })}</span>
              </div>
              <div className="cmpx-lens-cell emph">
                <b>{fmtOne(s.coversPerOutlet)}</b>
                <span>
                  {benchmark && benchmark !== s ? (
                    <Delta value={signedPct(s.coversPerOutlet, benchmark.coversPerOutlet)} size={11} />
                  ) : (
                    t('compare.benchmark')
                  )}
                </span>
              </div>
              <div className="cmpx-lens-cell emph">
                <b>{fmtTwo(s.coversPerSeat)}</b>
                <span>
                  {benchmark && benchmark !== s ? (
                    <Delta value={signedPct(s.coversPerSeat, benchmark.coversPerSeat)} size={11} />
                  ) : (
                    t('compare.benchmark')
                  )}
                </span>
              </div>
              <div className="cmpx-lens-cell">
                <b>{t('compare.noshowPct', { pct: s.noShow })}</b>
                <span>
                  {t('compare.cancelPct', { pct: s.cancel })}
                  {benchmark && benchmark !== s
                    ? ` · ${signedPp(s.noShow, benchmark.noShow) >= 0 ? '+' : ''}${t(
                        'compare.ppNoshow',
                        { pp: signedPp(s.noShow, benchmark.noShow) },
                      )}`
                    : ''}
                </span>
              </div>
              <div className="cmpx-lens-cell">
                <b>{t('compare.perOutlet', { value: fmtOne(s.latestCoversPerOutlet) })}</b>
                <span>
                  <Delta value={s.momentum} size={11} />
                  {' '}{t('compare.vsPrevious')}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function MetricContext({
  metric,
  summary,
  benchmark,
}: {
  metric: PerformanceMetric;
  summary: ScopeSummary;
  benchmark?: ScopeSummary;
}) {
  const { t } = useTranslation('performance');
  const key = metric.key;
  if (key === 'covers') {
    return (
      <span className="cmpx-context">
        {t('compare.perOutletSeat', {
          outlet: fmtOne(summary.coversPerOutlet),
          seat: fmtTwo(summary.coversPerSeat),
        })}
      </span>
    );
  }
  if (key === 'reservations') {
    return (
      <span className="cmpx-context">
        {t('compare.perOutletShare', {
          outlet: fmtOne(summary.reservationsPerOutlet),
          share: summary.share,
        })}
      </span>
    );
  }
  if (key === 'noshow' || key === 'cancel') {
    const ref = key === 'noshow' ? benchmark?.noShow : benchmark?.cancel;
    if (ref == null || benchmark === summary) {
      return <span className="cmpx-context">{t('compare.rateNeutral')}</span>;
    }
    const pp = signedPp(metric.value, ref);
    return (
      <span className="cmpx-context">
        {pp >= 0 ? '+' : ''}
        {t('compare.ppVsBench', { pp })}
      </span>
    );
  }
  if (key === 'turns' || key === 'seatTime') {
    return <span className="cmpx-context">{t('compare.capacityNorm')}</span>;
  }
  if (key === 'rejected') {
    return (
      <span className="cmpx-context">
        {t('compare.perOutlet', { value: fmtOne(safeDiv(metric.value, summary.outletCount)) })}
      </span>
    );
  }
  if (key === 'sellitems') {
    return (
      <span className="cmpx-context">
        {t('compare.perCover', { value: metric.value ? fmt.money(safeDiv(metric.value, summary.covers)) : '€0' })}
      </span>
    );
  }
  return null;
}

function CompareOutletTable({ items }: { items: ComparisonPerfItem[] }) {
  const { t } = useTranslation('performance');
  const rows = items
    .flatMap(item =>
      (item.perf?.outletRows ?? []).map(row => ({ item, row })),
    )
    .sort((a, b) => b.row.periodCovers - a.row.periodCovers);
  if (rows.length === 0) return null;

  const shown = rows.slice(0, 15);

  return (
    <div className="ol-card optbl-card">
      <div className="optbl-hint">
        <Icon name="info" size={14} />
        <span dangerouslySetInnerHTML={{ __html: t('compare.outletHint') }} />
      </div>
      <div className="optbl-scroll">
        <div
          className="opt-head"
          style={{ gridTemplateColumns: '2fr 1fr 0.8fr 0.8fr 0.8fr' }}
        >
          <span className="opt-h l">{t('metrics.outlet')}</span>
          <span className="opt-h">{t('compare.guests')}</span>
          <span className="opt-h">{t('compare.noshow')}</span>
          <span className="opt-h">{t('compare.cancel')}</span>
          <span className="opt-h">{t('compare.vsPrior')}</span>
        </div>
        {shown.map(({ item, row }) => (
          <div
            key={`${item.filter}:${row.outlet.id}`}
            className="opt-row"
            style={{ gridTemplateColumns: '2fr 1fr 0.8fr 0.8fr 0.8fr' }}
          >
            <div className="opt-name">
              <span
                className="cmp-chip-dot"
                style={{ background: item.color, marginRight: 9 }}
              />
              <div>
                <b>{row.outlet.name}</b>
                <span>{item.label}</span>
              </div>
            </div>
            <div className="opt-cell">
              <span className="opt-v">{fmt.num(row.periodCovers)}</span>
            </div>
            <div className="opt-cell">
              <span className="opt-v">{row.periodNoshow}%</span>
            </div>
            <div className="opt-cell">
              <span className="opt-v">{row.periodCancel}%</span>
            </div>
            <div className="opt-cell">
              <span
                className="opt-v"
                style={{
                  color:
                    row.vsPriorPct >= 0
                      ? 'var(--st-steady)'
                      : 'var(--st-alert)',
                }}
              >
                {row.vsPriorPct >= 0 ? '+' : ''}
                {row.vsPriorPct}%
              </span>
            </div>
          </div>
        ))}
      </div>
      {rows.length > shown.length && (
        <div
          className="optbl-hint"
          style={{ borderTop: '1px solid var(--line)' }}
        >
          {t('compare.moreOutlets', { count: rows.length - shown.length })}
        </div>
      )}
    </div>
  );
}

function CompareSeries({ items }: { items: ComparisonPerfItem[] }) {
  const { t } = useTranslation('performance');
  // Union of bucket keys across scopes, ordered by the first scope that has
  // them (all scopes share the same global aggregation + frame).
  const bucketOrder: string[] = [];
  const labelByKey = new Map<string, string>();
  items.forEach(i =>
    (i.perf?.series ?? []).forEach(b => {
      if (!labelByKey.has(b.key)) {
        labelByKey.set(b.key, b.label);
        bucketOrder.push(b.key);
      }
    }),
  );
  if (bucketOrder.length === 0) return null;

  const aggLabel = items.find(i => i.perf)?.perf?.aggregationLabel ?? t('compare.period');
  const frameLabel =
    items.find(i => i.perf)?.perf?.frameLabel?.toLowerCase() ?? '';
  const outletCountByFilter = new Map(
    items.map(item => [
      item.filter,
      activeOutlets(item.perf?.outlets ?? []).length || 1,
    ]),
  );

  const maxCoversPerOutlet = Math.max(
    ...items.flatMap(i => {
      const outletCount = outletCountByFilter.get(i.filter) ?? 1;
      return i.perf?.series?.map(b => safeDiv(b.covers, outletCount)) ?? [0];
    }),
    1,
  );

  return (
    <div className="ol-card" style={{ marginBottom: 22 }}>
      <div className="ol-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="activity" size={16} style={{ color: 'var(--accent)' }} />
          <h3>{t('compare.guestsPerOutlet', { unit: aggLabel })}</h3>
        </div>
        <span className="ol-conf">{t('compare.normalized', { frame: frameLabel })}</span>
      </div>
      <div className="histo">
        {bucketOrder.map(key => (
          <div className="histo-col" key={key}>
            <div className="histo-barwrap">
              <div className="cmpx-histo-group">
                {items.map(item => {
                  const pt = item.perf?.series?.find(b => b.key === key);
                  const outletCount = outletCountByFilter.get(item.filter) ?? 1;
                  const coversPerOutlet = safeDiv(pt?.covers ?? 0, outletCount);
                  const h = (coversPerOutlet / maxCoversPerOutlet) * 100;
                  return (
                    <span
                      key={item.filter}
                      className="cmpx-histo-bar"
                      style={{
                        height: `${h}%`,
                        background: item.color,
                      }}
                      title={t('compare.barTitle', {
                        label: item.label,
                        per: fmtOne(coversPerOutlet),
                        total: fmt.num(pt?.covers ?? 0),
                      })}
                    />
                  );
                })}
              </div>
            </div>
            <div className="histo-l">{labelByKey.get(key)}</div>
          </div>
        ))}
      </div>
      <div className="cmpx-legend">
        {items.map(item => (
          <span key={item.filter} className="kcl-leg">
            <span
              className="cmp-chip-dot"
              style={{ background: item.color }}
            />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}
